import { useMemo, useState, type DragEvent, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PersonId, RequestId } from '@shared/brand';
import { seedColorForSubArea } from '@domain/entities/Organization';
import {
  isOverdueUndecided,
  pendingSignersFor,
  signatureTrail,
  statusAfterApproval,
} from '@domain/policies/ApprovalPolicy';
import { groupRescheduleChains } from '../Requests/rescheduleChains';
import { reasonsForScope } from '@domain/entities/RuntimeConfig';
import { awaitsReplacement, type VacationRequest } from '@domain/entities/VacationRequest';
import { formatStoredPhone } from '@domain/value-objects/PhoneNumber';
import {
  BOARD_COLUMNS,
  CERTIFICATE_BOARD_COLUMNS,
  PERMIT_BOARD_COLUMNS,
  type RequestStatus,
} from '@domain/vocabulary';
import { getServices } from '@infrastructure/services';
import {
  Avatar,
  Banner,
  Button,
  Card,
  Chip,
  EmptyState,
  Field,
  Modal,
  MonthSwitcher,
  Spinner,
  cssVars,
  inputClass,
  cx,
} from '../../design-system';
import { formatBusinessDays, formatDateRange, formatDay, formatMonth } from '../../format';
import { useAccess } from '../../hooks/useAccess';
import { useApprovalScope } from '../../hooks/useApprovalScope';
import { useOwnAreaName } from '../../hooks/useAreaName';
import { useSession } from '../../providers/SessionProvider';
import { useToast } from '../../providers/ToastProvider';
import { liveBalanceRefsFor, liveBalancesByPerson, totalAvailable } from '../../balancePeriod';
import shared from '../shared.module.css';
import { AbsenceTypePicker } from '../AbsenceTypePicker';
import { CertificateBoard, PermitBoard } from './AbsenceBoards';
import { RequestDocumentActions } from './RequestDocument';
import { belongsToBoardMonth, sameMonth } from './boardMonth';
import { ReasonPicker } from '../ReasonPicker';
import styles from './ApprovalsScreen.module.css';
import { loadFailed } from '../../loadError';
import { usePersonLink } from '../../hooks/usePersonLink';
import { PageTitle } from '../../shell/PageTitle';

/**
 * Aprobaciones — transcribed from `design/reference/screens/aprobaciones.html`.
 *
 * A kanban board with drag and drop. One constraint, enforced in the callable and
 * mirrored here: a drag only moves a card between the *review* columns. Approving
 * or rejecting is a decision that needs a signature and, for a rejection, a reason
 * from the catalogue — so dropping onto «Aprobada» or «Rechazada» opens the
 * decision flow instead of silently applying it.
 *
 * The prototype drives the board with ← / → buttons as well as the drag, which is
 * the only path a keyboard user has; both are wired to the same transitions.
 */

/** The prototype's `INK` map: a readable text colour for each column tint. */
const INK: Readonly<Record<string, string>> = {
  '#7C5CFF': '#5B3FD4',
  '#E8930C': '#8A5A05',
  '#38619F': '#2A4E86',
  '#12A594': '#0B7466',
  '#E5484D': '#A82B30',
};

const inkFor = (color: string): string => INK[color] ?? color;

/** Los tres tableros de esta pantalla, y el único sitio que decide cuál se ve. */
type BoardKind = 'vacaciones' | 'permisos' | 'incapacidades';

/**
 * A partir de cuántas tarjetas una columna pliega las suyas.
 *
 * La tarjeta abierta mide entre 325 y 540px según lo que traiga —doble visto
 * bueno, motivo del rechazo, nota del respaldo—, así que a la cuarta la columna
 * ya no cabe en la ventana y arrastrar de la primera a otra columna obliga a
 * mover la página mientras se sostiene el botón. Tres es lo que se alcanza a ver
 * de un vistazo; de ahí en adelante la columna se lee como índice y se abre lo
 * que interese. Plegada mide 54px.
 *
 * El umbral vive en la columna y no en la tarjeta a propósito: una tarjeta no
 * sabe cuántas hermanas tiene, y el problema no es la tarjeta larga sino la
 * columna larga.
 */
const CARDS_BEFORE_FOLDING = 3;

/** Lo que una tarjeta plegada necesita saber. `null` en una columna corta. */
interface Fold {
  readonly folded: boolean;
  readonly onToggle: () => void;
}

/**
 * El chevron de plegar, dibujado y no escrito.
 *
 * Empezó siendo el carácter `▾`, y se veía mal: un triángulo relleno cuyo trazo,
 * tamaño y posición sobre la línea los decide la fuente que haya resuelto el
 * sistema, no la hoja de estilos. Un icono que cambia de forma según la máquina
 * no es un icono. Va en SVG por lo mismo que la lupa y la campana del shell:
 * hereda `currentColor`, gira sin arrastrar espacios en blanco alrededor, y se
 * ve igual en todas partes.
 */
const ChevronGlyph = (): ReactNode => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

/**
 * Cuántas columnas dibuja el tablero activo.
 *
 * Alimenta `--board-columns`, que es de donde la hoja de estilos saca el ancho
 * que esta pantalla puede pedir. Se cuenta sobre las mismas listas que dibujan
 * los tableros, para que agregar o quitar una columna no deje el ancho mintiendo
 * —y con él, una columna otra vez debajo de las demás.
 */
const columnCountOf = (kind: BoardKind): number =>
  kind === 'permisos'
    ? PERMIT_BOARD_COLUMNS.length
    : kind === 'incapacidades'
      ? CERTIFICATE_BOARD_COLUMNS.length
      : BOARD_COLUMNS.length;

/**
 * Where the → button sends a card.
 *
 * It is the next column, except that «Visto bueno» is skipped for a request
 * that only needs one signature — otherwise a single-leader request would sit
 * in a column that, for it, means nothing.
 */
const forwardTarget = (
  index: number,
  requiredSignatures: number,
): { status: RequestStatus; color: string } | null => {
  const next = BOARD_COLUMNS[index + 1];
  if (next === undefined) return null;
  if (next.status === 'Visto bueno' && requiredSignatures < 2) {
    const approved = BOARD_COLUMNS.find((c) => c.status === 'Aprobada');
    return approved === undefined ? null : { status: approved.status, color: approved.color };
  }
  return { status: next.status, color: next.color };
};

export const ApprovalsScreen = (): ReactNode => {
  const session = useSession();
  const { featureOn, holds } = useAccess();
  /* Aprobaciones no sigue al selector: lo que se decide aquí sale de
     `useApprovalScope`, que es lo que *esta persona* puede firmar. El nombre
     tiene que decir lo mismo que la lista, o el encabezado miente. */
  const areaName = useOwnAreaName();
  const approval = useApprovalScope();
  const location = useLocation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const today = getServices().clock.today();
  /* El mes a la vista. Arranca en el de hoy, que es donde está el trabajo. */
  const [monthAnchor, setMonthAnchor] = useState(today);

  const [draggingId, setDraggingId] = useState<RequestId | null>(null);
  const [overColumn, setOverColumn] = useState<RequestStatus | null>(null);
  const [rejecting, setRejecting] = useState<VacationRequest | null>(null);
  /* La solicitud que se está cerrando fuera de tiempo, si hay alguna. */
  const [resolving, setResolving] = useState<VacationRequest | null>(null);
  const [reopening, setReopening] = useState<{
    request: VacationRequest;
    status: 'Por revisar' | 'En revisión';
  } | null>(null);
  const [expanded, setExpanded] = useState<RequestId | null>(null);
  const [waiving, setWaiving] = useState<VacationRequest | null>(null);
  /*
   * Las que el líder abrió a mano en una columna plegada.
   *
   * Se guarda lo abierto y no lo cerrado porque el estado de reposo es plegado:
   * una tarjeta que llega nueva a una columna llena entra plegada como las
   * demás, sin que nadie tenga que cerrarla. Y se recuerda por id, así que una
   * que se abrió y se movió de columna sigue abierta donde caiga.
   */
  const [unfolded, setUnfolded] = useState<ReadonlySet<RequestId>>(new Set<RequestId>());

  const toggleFold = (id: RequestId): void => {
    setUnfolded((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  };

  /*
   * Which kinds of absence this session can act on.
   *
   * Only what the viewer can actually decide: `validateIncapacity` refuses anyone
   * who is not an administrator, so a leader is never shown a certificate column
   * they would have no button for. RR.HH. holds `admin` and not `aprobar`, so for
   * them the reverse is true and the board opens on certificates.
   */
  const availableTypes = useMemo(
    () =>
      /*
       * The same three cards «Mis ausencias» uses, and the same glyphs and
       * colours, because they are the same three things seen from the other side
       * of the desk. A leader who has just filed their own request should not have
       * to re-learn the control to decide somebody else's.
       */
      [
        {
          id: 'vacaciones' as const,
          title: 'Vacaciones',
          note: 'Solicitudes por decidir',
          glyph: '☀',
          color: 'var(--color-brand)',
          allowed: holds('aprobar'),
        },
        {
          id: 'permisos' as const,
          title: 'Home office y permisos',
          note: 'Remoto, horas, día personal',
          glyph: '⌂',
          color: 'var(--color-success)',
          allowed: holds('aprobar') && featureOn('permisos'),
        },
        {
          id: 'incapacidades' as const,
          title: 'Incapacidades',
          note: 'IMSS y certificados',
          glyph: '✚',
          color: 'var(--color-warning)',
          allowed: holds('admin') && featureOn('incapacidades'),
        },
      ].filter((entry) => entry.allowed),
    [holds, featureOn],
  );

  const [type, setType] = useState<BoardKind | null>(null);

  /*
   * `?tipo=` opens the board on a specific chip.
   *
   * It is how a row in «Pendientes de tu aprobación» lands somewhere useful: its
   * button used to drop the leader on whichever chip came first, so a permit sent
   * them to the vacation kanban and the permit was nowhere on it. Ignored once they
   * pick a chip themselves — the URL states where they arrived, not where they are.
   */
  const requested = new URLSearchParams(location.search).get('tipo');
  const fromUrl = availableTypes.find((entry) => entry.id === requested)?.id;

  // Falls back to the first kind this session can act on, which is what makes the
  // screen open on certificates for RR.HH. and on the vacation board for a leader.
  const activeType = type ?? fromUrl ?? availableTypes[0]?.id ?? 'vacaciones';

  const board = useQuery({
    // Scoped to what this session may decide on, and keyed on that scope: a
    // leader sees their own sub area, an administrator the whole area.
    queryKey: ['approvals', approval.scope, session.personId],
    // Waits for the leadership links. Filtering before they land would cache a
    // board with nothing on it and never recompute.
    enabled: activeType === 'vacaciones' && !approval.loading,
    queryFn: async () => {
      const result = await getServices().requests.list(
        {
          ...approval.scope,
          /*
           * «Cancelada» is fetched and has no column, which is deliberate.
           *
           * The board draws five columns and that is not one of them, so a
           * cancelled request renders nowhere either way. What it is here for is
           * the chain: a request that was refused and then replaced is only
           * recognisable as superseded if its successor is in the same result
           * set. Without the cancelled head, the leader kept a card for
           * VC-0006 — refused, replaced, and the replacement withdrawn — a story
           * that ended days ago and still asked to be looked at.
           */
          statuses: [
            'Por revisar',
            'En revisión',
            'Visto bueno',
            'Aprobada',
            'Rechazada',
            'Cancelada',
          ],
        },
        { limit: 100 },
      );
      if (!result.ok) throw loadFailed('No pudimos cargar el tablero.', result.error);
      // The people this session may decide on, and never themselves — a card
      // offering a leader an approve button on their own request is precisely
      // what the second signature exists to prevent.
      return approval.mine(result.value.items);
    },
  });

  /*
   * One card per story here too, and on this screen it prevents a mistake as
   * well as a mess.
   *
   * A rescheduled request leaves its refused predecessors behind, and the board
   * filed them under «Rechazada» — where a leader can drag a card back out to
   * reconsider it. Reopening a request that has already been replaced would put
   * two live requests on the same holiday, both consuming the same days. The
   * chain collapses to the request that is still live; the ones it replaced are
   * history, readable inside the card and not draggable.
   */
  const chains = useMemo(() => groupRescheduleChains(board.data ?? []), [board.data]);
  /*
   * Todas las tarjetas vivas, antes de filtrar por mes.
   *
   * Se conserva la lista entera para poder decir cuántas aprobadas viven en otro
   * mes. Sin ese número, una columna «Aprobada» vacía se lee como que nadie tiene
   * vacaciones y no como que están en septiembre. Ver `boardMonth.ts`.
   */
  const allCards = useMemo(() => chains.map((chain) => chain.head), [chains]);

  const cards = useMemo(
    () => allCards.filter((card) => belongsToBoardMonth(card, monthAnchor)),
    [allCards, monthAnchor],
  );
  const historyByRequest = useMemo(
    () => new Map(chains.map((chain) => [chain.head.id, chain.history])),
    [chains],
  );

  /**
   * Balances for everyone on the board, in one read.
   *
   * The design shows «saldo N» on each card. Resolving that per card would be an
   * N+1 against a roster of 500 people, so the ids are collected first and fetched
   * together.
   */
  /*
   * El padrón del área, para saber en qué periodo está cada quien.
   *
   * El saldo se guarda por periodo de aniversario, así que pedirlo requiere la
   * fecha de ingreso de cada persona; la tarjeta sólo trae el nombre. Es la misma
   * clave que ya usa cada tarjeta para resolver los nombres de los líderes, de
   * modo que la consulta se comparte en vez de repetirse.
   */
  const roster = useQuery({
    queryKey: ['approvals', 'peopleNames', session.areaId],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const result = await getServices().people.list(
        { areaId: session.areaId, status: 'active' },
        { limit: 200 },
      );
      return result.ok ? result.value.items : [];
    },
  });

  const balances = useQuery({
    queryKey: [
      'approvals',
      'balances',
      cards.map((r) => r.personId).join(','),
      (roster.data ?? []).length,
    ],
    enabled: cards.length > 0 && (roster.data ?? []).length > 0,
    queryFn: async () => {
      const ids = [...new Set(cards.map((r) => r.personId))];
      /* Lo que la persona puede pedir hoy entre todos sus periodos vigentes: con
         «Vencimiento del saldo» en más de 12 meses, lo que queda del anterior
         sigue valiendo y es lo primero que se gasta. */
      const today = getServices().clock.today();
      const expiryMonths = session.config.rules.expiryMonths;
      const people = (roster.data ?? []).filter((person) => ids.includes(person.id));
      const result = await getServices().balances.getMany(
        liveBalanceRefsFor(people, today, expiryMonths),
      );
      if (!result.ok) return new Map<PersonId, number>();
      const byPerson = liveBalancesByPerson(people, result.value, today, expiryMonths);
      return new Map([...byPerson].map(([id, lives]) => [id, totalAvailable(lives)]));
    },
  });

  /** The area's leaders, for the banner's count and each card's project labels. */
  const leaders = useQuery({
    queryKey: ['approvals', 'leaders', session.areaId],
    queryFn: async () => {
      const result = await getServices().leaders.listByArea(session.areaId);
      return result.ok ? result.value : [];
    },
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['approvals'] });
  };

  const boardKey = ['approvals', approval.scope, session.personId];

  /**
   * Moves the card now and asks the server after.
   *
   * A drag that waits for the round trip before the card lands reads as a
   * dropped drag: the finger lets go, nothing happens, and the natural reaction
   * is to drag it again — which is how the same decision gets sent twice. So the
   * board is patched first and reconciled later.
   *
   * The in-flight refetch is cancelled before patching. Without that, a response
   * that was already on its way lands after the patch and puts the card back
   * where it was, which looks exactly like the failure this is meant to make
   * visible. Returns the undo, so a refusal restores the *previous* board rather
   * than a guess at what it held.
   */
  const showMoved = async (id: RequestId, status: RequestStatus): Promise<() => void> => {
    await queryClient.cancelQueries({ queryKey: boardKey });
    const previous = queryClient.getQueryData<readonly VacationRequest[]>(boardKey);

    queryClient.setQueryData<readonly VacationRequest[]>(boardKey, (current) =>
      (current ?? []).map((card) => (card.id === id ? { ...card, status } : card)),
    );

    return () => {
      if (previous !== undefined) queryClient.setQueryData(boardKey, previous);
    };
  };

  const approve = async (request: VacationRequest, waiveReason?: string): Promise<void> => {
    // Where the server will put it, derived from the column it is in rather than
    // assumed to be «Aprobada»: a first signature on a two-signature request
    // lands in «Visto bueno», and moving it to the wrong column only to correct
    // it a second later is worse than not moving it at all.
    const landing = statusAfterApproval(request);
    const undo = await showMoved(request.id, landing);

    const result = await getServices().requests.decide({
      requestId: request.id,
      decision: 'approve',
      reason: null,
      suggestedRanges: [],
      waiveReplacementReason: waiveReason ?? null,
    });

    if (!result.ok) {
      undo();
      toast.bad('No se pudo registrar la aprobación.');
      return;
    }

    /*
     * A first signature and a completed approval feel like different outcomes to
     * the leader, so the message distinguishes them — and it distinguishes them
     * by where the request *landed*, not by how many signatures it needs. The
     * second signer on a two-signature request was being told «falta la segunda
     * firma» about the one they had just given.
     */
    toast.ok(
      landing === 'Aprobada'
        ? `${request.folio} quedó aprobada.`
        : `Diste tu visto bueno a ${request.folio}. Falta la segunda firma.`,
    );
    await refresh();
  };

  const moveTo = async (request: VacationRequest, status: RequestStatus): Promise<void> => {
    if (request.status === status) return;

    /*
     * Moving a decided card is never a plain move, so every route out of a
     * terminal column asks for a catalogue reason first — it is the transition
     * somebody will have to explain later.
     *
     * «Rechazada» from an approved card revokes it. The review columns pull it
     * back to be decided again, clearing the signatures and re-committing the days.
     * Nothing goes straight to «Aprobada»: granting has to collect its signatures,
     * and a drag that skipped them would make the double sign-off optional.
     */
    if (request.status === 'Aprobada' || request.status === 'Rechazada') {
      if (status === 'Rechazada' && request.status === 'Aprobada') setRejecting(request);
      else if (status === 'Por revisar' || status === 'En revisión') {
        setReopening({ request, status });
      }
      return;
    }

    if (status === 'Rechazada') {
      setRejecting(request);
      return;
    }

    if (status === 'Aprobada' || status === 'Visto bueno') {
      /*
       * El candado del respaldo, antes de firmar.
       *
       * Aprobar es decir «alguien cubre estos días». Mientras la persona
       * nombrada no lo haya confirmado eso es una suposición, así que el
       * diálogo pide por escrito por qué se aprueba de todos modos. El servidor
       * lo vuelve a comprobar: esto evita el viaje, no es lo que manda.
       */
      if (awaitsReplacement(request)) {
        setWaiving(request);
        return;
      }
      await approve(request);
      return;
    }

    const undo = await showMoved(request.id, status);

    const result = await getServices().requests.moveToColumn(request.id, status);
    if (!result.ok) {
      undo();
      // Says where it went back to, because a card that reappears in its old
      // column with a generic «no se pudo» reads as the drag never happening.
      toast.bad(`No se pudo mover ${request.folio}. Volvió a «${request.status}».`);
      return;
    }
    await refresh();
  };

  const onDrop = async (status: RequestStatus): Promise<void> => {
    setOverColumn(null);
    const id = draggingId;
    setDraggingId(null);
    if (id === null) return;

    const request = cards.find((r) => r.id === id);
    if (request === undefined) return;

    /*
     * Soltar una tarjeta en su propia columna no es moverla.
     *
     * Sin esto, levantar una tarjeta de «Observaciones» y dejarla donde estaba
     * abría el modal de decisión como si la hubieran cambiado de columna — y
     * quien sólo quería reacomodar la vista se encontraba pidiendo un motivo para
     * algo que no hizo. Basta con arrastrar unos píxeles sin querer.
     */
    if (request.status === status) return;

    await moveTo(request, status);
  };

  /* Lo que espera una decisión. El mes no lo toca: `belongsToBoardMonth` sólo
     archiva lo aprobado, así que estas están en `cards` mire uno el mes que mire. */
  const pending = cards.filter((r) => r.status !== 'Aprobada' && r.status !== 'Rechazada');
  const showingThisMonth = sameMonth(monthAnchor, today);
  const dual = pending.filter((r) => r.requiredSignatures === 2);
  const awaitingSecond = cards.filter((r) => r.status === 'Visto bueno').length;
  /* Lo aprobado que no se ve porque pertenece a otro mes. Cero significa que el
     mes a la vista es todo lo que hay, no que el tablero esté vacío. */
  const approvedElsewhere =
    allCards.filter((r) => r.status === 'Aprobada').length -
    cards.filter((r) => r.status === 'Aprobada').length;
  const leaderCount = new Set((leaders.data ?? []).map((l) => l.leaderPersonId)).size;

  return (
    <div
      className={cx(shared.screen, styles.fitColumns)}
      style={cssVars({ '--board-columns': String(columnCountOf(activeType)) })}
    >
      <div>
        <PageTitle className={styles.title}>Aprobaciones</PageTitle>
        <p className={styles.note}>
          {activeType !== 'vacaciones'
            ? // Only points at «Mis ausencias» for someone who has one: RR.HH.
              // does not hold `solicitar`, so for them that section does not exist.
              holds('solicitar')
              ? 'Las ausencias de tu equipo. Lo tuyo vive en Mis ausencias.'
              : 'Las ausencias del área que esperan tu revisión.'
            : /* El mes acota una sola columna, y conviene decirlo: si no, un
                 tablero con «Aprobada» vacía se lee como que el filtro se llevó
                 todo. Lo que espera revisión se ve igual en cualquier mes. */
              `${
                pending.length === 0
                  ? 'No hay solicitudes esperando tu revisión.'
                  : `${String(pending.length)} ${
                      pending.length === 1 ? 'solicitud espera' : 'solicitudes esperan'
                    } tu revisión. Arrastra las tarjetas de una columna a otra para cambiar su estado.`
              }${
                showingThisMonth
                  ? ''
                  : ` En «Aprobada» ves sólo las de ${formatMonth(monthAnchor)}; lo demás se queda a la vista.`
              }`}
        </p>
      </div>

      {/* One board per kind of absence rather than one board filtered three ways:
          their statuses genuinely differ, so a shared column set would show columns
          that can never hold a card. Hidden when there is only one kind to show —
          a segmented control with a single segment is decoration. */}
      {availableTypes.length > 1 && (
        <AbsenceTypePicker
          options={availableTypes}
          active={activeType}
          onSelect={setType}
          label="Tipo de ausencia por aprobar"
        />
      )}

      {activeType === 'permisos' && <PermitBoard canDecide={holds('aprobar')} />}
      {activeType === 'incapacidades' && <CertificateBoard canValidate={holds('admin')} />}

      {activeType === 'vacaciones' && !featureOn('kanban') && (
        <Card title="Aprobaciones">
          <Banner tone="info">
            El tablero kanban está apagado. Enciéndelo en Features y módulos para arrastrar
            tarjetas entre columnas.
          </Banner>
        </Card>
      )}

      {/* The double sign-off explainer. Only shown when the board actually contains
          a request that needs two signatures — otherwise it explains a rule nobody
          on this screen is subject to. */}
      {activeType === 'vacaciones' && dual.length > 0 && (
        <div className={styles.dualBand}>
          <span className={styles.dualGlyph} aria-hidden="true">
            ✓✓
          </span>
          <p className={styles.dualNote}>
            El área {areaName} tiene {leaderCount} líderes. Cuando un colaborador trabaja en
            proyectos de más de un líder, la aprobación de uno no basta: el segundo líder da el
            visto bueno antes de que la solicitud quede aprobada.
          </p>
          <Chip background="var(--color-brand-bg)" foreground="var(--color-brand-ink)">
            {awaitingSecond} esperando el 2º visto bueno
          </Chip>
        </div>
      )}

      {/*
        El mes del tablero, sólo sobre el de vacaciones.
        
        Los otros dos tableros —permisos e incapacidades— no lo llevan: un permiso
        de horas y un certificado del IMSS se resuelven en días, no se piden con
        meses de anticipación, y ahí la pila no se acumula. Añadirles un cambiador
        sería un control que nunca cambia nada.
      */}
      {activeType === 'vacaciones' && featureOn('kanban') && (
        <div className={styles.monthBar}>
          <MonthSwitcher
            month={monthAnchor}
            today={today}
            onChange={setMonthAnchor}
            label="Mes del tablero"
          />
          {/* Lo aprobado que vive en otro mes, dicho aquí y no escondido: sin este
              número, un tablero con la columna «Aprobada» vacía se lee como que
              nadie tiene vacaciones, no como que están en septiembre. */}
          {approvedElsewhere > 0 && (
            <span className={styles.monthAside}>
              {approvedElsewhere}{' '}
              {approvedElsewhere === 1 ? 'ausencia aprobada' : 'ausencias aprobadas'} en otros
              meses
            </span>
          )}
        </div>
      )}

      {activeType === 'vacaciones' && featureOn('kanban') && (board.isLoading ? (
        <Card>
          <Spinner label="Cargando el tablero" />
        </Card>
      ) : board.error !== null ? (
        <Banner tone="danger">No pudimos cargar el tablero.</Banner>
      ) : (
        <div className={styles.board}>
          {BOARD_COLUMNS.map((column, index) => {
            const columnCards = cards.filter((r) => r.status === column.status);
            const over = overColumn === column.status;
            const folding = columnCards.length > CARDS_BEFORE_FOLDING;

            return (
              <section
                key={column.status}
                className={cx(styles.column, over && styles.columnOver)}
                style={cssVars({
                  '--column-color': column.color,
                  '--column-ink': inkFor(column.color),
                })}
                onDragOver={(event: DragEvent) => {
                  event.preventDefault();
                  setOverColumn(column.status);
                }}
                onDragLeave={() => {
                  setOverColumn((current) => (current === column.status ? null : current));
                }}
                onDrop={(event: DragEvent) => {
                  event.preventDefault();
                  void onDrop(column.status);
                }}
              >
                <header className={styles.columnHeader}>
                  <span className={styles.columnDot} aria-hidden="true" />
                  <span className={styles.columnTitle}>{column.status}</span>
                  <span className={styles.columnCount}>{columnCards.length}</span>
                </header>

                <div className={styles.cards}>
                  {columnCards.map((request) => (
                    <BoardCard
                      key={request.id}
                      request={request}
                      history={historyByRequest.get(request.id) ?? []}
                      columnIndex={index}
                      columnColor={column.color}
                      balanceDays={balances.data?.get(request.personId) ?? null}
                      canDecide={holds('aprobar')}
                      overdue={isOverdueUndecided(request, getServices().clock.today())}
                      onResolveOverdue={() => {
                        setResolving(request);
                      }}
                      dragging={draggingId === request.id}
                      expanded={expanded === request.id}
                      fold={
                        folding
                          ? {
                              folded: !unfolded.has(request.id),
                              onToggle: () => {
                                toggleFold(request.id);
                              },
                            }
                          : null
                      }
                      onDragStart={() => {
                        setDraggingId(request.id);
                      }}
                      onDragEnd={() => {
                        setDraggingId(null);
                        setOverColumn(null);
                      }}
                      onToggleDetail={() => {
                        setExpanded((current) => (current === request.id ? null : request.id));
                      }}
                      onMove={(status) => {
                        void moveTo(request, status);
                      }}
                    />
                  ))}

                  <div className={cx(styles.dropZone, over && styles.dropZoneOver)}>
                    {over
                      ? 'Suelta aquí'
                      : columnCards.length > 0
                        ? 'Arrastra una tarjeta'
                        : 'Sin solicitudes'}
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      ))}

      {resolving !== null && (
        <OverdueDialog
          request={resolving}
          onClose={() => {
            setResolving(null);
          }}
          onDone={async () => {
            setResolving(null);
            await refresh();
          }}
        />
      )}

      {reopening !== null && (
        <ReopenDialog
          request={reopening.request}
          status={reopening.status}
          onClose={() => {
            setReopening(null);
          }}
          onDone={async () => {
            setReopening(null);
            await refresh();
          }}
        />
      )}

      {waiving !== null && (
        <WaiveReplacementDialog
          request={waiving}
          onClose={() => {
            setWaiving(null);
          }}
          onConfirm={async (reason) => {
            const request = waiving;
            setWaiving(null);
            await approve(request, reason);
          }}
        />
      )}

      {rejecting !== null && (
        <RejectDialog
          request={rejecting}
          onClose={() => {
            setRejecting(null);
          }}
          onDone={async () => {
            setRejecting(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
};

const BoardCard = ({
  request,
  history,
  columnIndex,
  columnColor,
  balanceDays,
  canDecide,
  overdue,
  onResolveOverdue,
  dragging,
  expanded,
  fold,
  onDragStart,
  onDragEnd,
  onToggleDetail,
  onMove,
}: {
  request: VacationRequest;
  /** The refused attempts this one replaced, most recent first. */
  history: readonly VacationRequest[];
  columnIndex: number;
  columnColor: string;
  balanceDays: number | null;
  canDecide: boolean;
  /** Se le pasaron las fechas sin decisión: ver `isOverdueUndecided`. */
  overdue: boolean;
  onResolveOverdue: () => void;
  dragging: boolean;
  expanded: boolean;
  /** Plegado, cuando la columna trae más tarjetas de las que caben. */
  fold: Fold | null;
  onDragStart: () => void;
  onDragEnd: () => void;
  onToggleDetail: () => void;
  onMove: (status: RequestStatus) => void;
}): ReactNode => {
  const openPerson = usePersonLink();
  const { holds } = useAccess();
  const session = useSession();

  /*
   * La solicitud propia se ve pero no se firma.
   *
   * Un líder no llega nunca a este caso: su cola viene filtrada por vínculo y
   * nadie puede ser su propio líder. Quien administra o configura ve el área
   * entera, así que **tiene su propia tarjeta delante**, y hasta ahora con los
   * botones encendidos: el servidor la rechazaba —«No puedes firmar tu propia
   * solicitud»— después de pulsarlos.
   *
   * Quitar los botones sin decir nada tampoco servía: se leía como que el tablero
   * se había roto, que es exactamente lo que se preguntó al verlo. Así que la
   * tarjeta lo dice.
   */
  const ownRequest = request.personId === session.personId;
  const mayDecide = canDecide && !ownRequest;

  const leaderLinks = useQuery({
    queryKey: ['approvals', 'links', request.personId],
    queryFn: async () => {
      const result = await getServices().leaders.leadersOf(request.personId, request.areaId);
      return result.ok ? result.value : [];
    },
  });

  const people = useQuery({
    queryKey: ['approvals', 'peopleNames', request.areaId],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const result = await getServices().people.list(
        { areaId: request.areaId, status: 'active' },
        { limit: 200 },
      );
      return result.ok ? result.value.items : [];
    },
  });

  const links = leaderLinks.data ?? [];
  const nameOfLeader = (id: PersonId): string =>
    (people.data ?? []).find((p) => p.id === id)?.displayName ?? '';

  const approvals = useQuery({
    queryKey: ['approvals', 'signatures', request.id],
    queryFn: async () => {
      const result = await getServices().requests.approvalsFor(request.id);
      return result.ok ? result.value : [];
    },
  });

  const workItems = useQuery({
    queryKey: ['approvals', 'work', request.id],
    queryFn: async () => {
      const result = await getServices().requests.workItemsFor(request.id);
      return result.ok ? result.value : [];
    },
  });

  const contacts = useQuery({
    queryKey: ['approvals', 'sos', request.personId],
    // Emergency contacts are third-party personal data: fetched only when the
    // detail is open *and* the viewer holds `ver_sos`.
    enabled: expanded && holds('ver_sos'),
    queryFn: async () => {
      const result = await getServices().contacts.listFor(request.personId);
      return result.ok ? result.value : [];
    },
  });

  const trail = signatureTrail(request.requiredSignatures, approvals.data ?? []);
  /* Quién ocupa cada hueco todavía sin firmar. Ver `pendingSignersFor`. */
  const pendientes = pendingSignersFor(trail, links.map((l) => l.leaderPersonId));
  const signed = trail.filter((segment) => segment.signed).length;

  /*
   * Quién la firmó, cuando pedía una sola firma.
   *
   * El bloque «Doble visto bueno» de abajo sólo se pinta con dos, y por buenas
   * razones —un hueco solo se lee como una barra de progreso de un paso—, pero
   * eso dejaba sin contestar «¿quién la aprobó?» en todas las de una firma, que
   * son la mayoría. El dato estaba guardado desde siempre: VC-0046 lleva su fila
   * con «Paulo Ramirez Contreras · approved» y la tarjeta no la enseñaba en
   * ningún sitio.
   *
   * Se nota más desde que el superusuario puede firmar sin ser líder: antes quien
   * firmaba salía en «Le firman», y ahora puede no estar ahí.
   *
   * Sólo sobre lo ya resuelto. Una cancelada puede arrastrar una firma anterior, y
   * «Aprobada por» sobre una solicitud que la persona canceló cuenta otra historia.
   */
  const resuelta =
    request.status === 'Aprobada' ||
    request.status === 'Rechazada' ||
    request.status === 'Tomada';

  const firma = trail.find(
    (segment) => segment.approverName !== null && (segment.signed || segment.rejected),
  );

  /**
   * Which project each approver signs for.
   *
   * Resolved from this person's own leader links rather than a global map: the
   * same leader can head different projects for different people, and the label
   * has to say why *this* signature is needed.
   */
  const projectFor = (approverName: string | null): string => {
    if (approverName === null) return 'Por asignar';
    const match = links.find((l) => nameOfLeader(l.leaderPersonId) === approverName);
    return match === undefined || match.projectLabel.length === 0
      ? 'Proyecto'
      : match.projectLabel;
  };
  const decided = request.status === 'Aprobada' || request.status === 'Rechazada';
  /*
   * A decided card is not a dead end: it can be pulled back into review, which is
   * why «Aprobada» and «Rechazada» stay draggable. The one thing that closes the
   * door is the calendar — once the dates have begun the person is taking the
   * days, and moving the card would rewrite a balance that payroll already acted
   * on. The callable refuses it too; this stops the card being picked up only to
   * fail on drop.
   */
  const reversible = decided && getServices().clock.today().isBefore(request.range.start);
  const work = workItems.data ?? [];
  const sos = contacts.data ?? [];
  const accent = seedColorForSubArea(request.subAreaName);
  const back = BOARD_COLUMNS[columnIndex - 1];
  const forward = forwardTarget(columnIndex, request.requiredSignatures);

  /*
   * The forward label previews the outcome. «Aprobar» and «Dar visto bueno» are
   * not the same event: with a first signature already in, the next one closes
   * the request, and calling both "approve" hides that from the person clicking.
   */
  const forwardLabel =
    forward === null
      ? '→'
      : forward.status === 'Aprobada'
        ? signed > 0
          ? 'Dar visto bueno →'
          : 'Aprobar →'
        : forward.status === 'Visto bueno'
          ? 'Aprobar →'
          : '→';

  const folded = fold !== null && fold.folded;

  /*
   * La cabecera es la misma plegada y abierta, y por eso vive aquí en vez de
   * dentro de cada rama: es lo único que la tarjeta plegada enseña, así que si
   * las dos versiones divergieran, plegar cambiaría lo que dice la tarjeta y no
   * sólo cuánto muestra.
   */
  const header = (
    <div className={styles.cardTop}>
      <Avatar
        name={request.personName}
        color={accent}
        size={34}
        onOpen={openPerson(request.personId)}
      />
      <div className={styles.cardWho}>
        <div className={styles.cardName}>{request.personName}</div>
        {/* Plegada, la línea de abajo dice cuándo. Recorrer una columna con la
            vista es preguntar quién y qué días; la sub área espera abierta. */}
        {folded ? (
          <div className={styles.foldedRange}>{formatDateRange(request.range)}</div>
        ) : (
          <div className={styles.cardSub}>{request.subAreaName}</div>
        )}
      </div>
      {/* El folio se va al plegar y vuelve al abrir. Con él puesto, la fila deja
          98px para el nombre y la fecha, y la fecha se parte en dos renglones:
          plegar dejaba de ahorrar alto justo donde importa. Nadie recorre una
          columna buscando un folio —se busca de quién es y qué días— y en cuanto
          la tarjeta se abre está ahí. */}
      {!folded && <span className={styles.cardCode}>{request.folio}</span>}
      {fold !== null && (
        <button
          type="button"
          className={cx(styles.foldToggle, folded && styles.foldToggleClosed)}
          aria-expanded={!folded}
          aria-label={
            folded
              ? `Ver la solicitud ${request.folio} de ${request.personName}`
              : `Plegar la solicitud ${request.folio} de ${request.personName}`
          }
          /* La tarjeta entera es un asa de arrastre: sin esto, empujar el
             chevron levanta la tarjeta en vez de abrirla. */
          draggable={false}
          onDragStart={(event: DragEvent) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={fold.onToggle}
        >
          <ChevronGlyph />
        </button>
      )}
    </div>
  );

  /*
   * Plegada sale por aquí. Es el mismo `<article>` en la misma posición, así que
   * React actualiza el nodo en vez de montarlo de nuevo: la tarjeta no pierde el
   * arrastre a medio gesto ni vuelve a pedir sus consultas al abrirse.
   */
  if (folded) {
    return (
      <article
        className={cx(
          styles.card,
          styles.cardFolded,
          decided && styles.cardDecided,
          dragging && styles.cardDragging,
        )}
        style={cssVars({ '--column-color': columnColor })}
        draggable={mayDecide && (!decided || reversible)}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        {header}
      </article>
    );
  }

  return (
    <article
      className={cx(
        styles.card,
        decided && styles.cardDecided,
        dragging && styles.cardDragging,
      )}
      style={cssVars({ '--column-color': columnColor })}
      draggable={mayDecide && (!decided || reversible)}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      {header}

      {/*
        * Se le pasaron las fechas y nadie la decidió.
        *
        * Mientras siga así aparta días por unas vacaciones que ya no van a ocurrir
        * como se pidieron, y aprobar o rechazar dejó de tener sentido: lo único que
        * queda por saber es si esa persona se fue. Por eso la tarjeta deja de
        * ofrecer el camino normal y ofrece éste.
        */}
      {overdue && (
        <div className={styles.overdue}>
          <div className={styles.overdueText}>
            Empezó el {formatDay(request.range.start)} y nadie la decidió. Sus días siguen
            apartados.
          </div>
          {mayDecide && (
            <Button
              variant="secondary"
              onClick={() => {
                onResolveOverdue();
              }}
            >
              Resolver
            </Button>
          )}
        </div>
      )}

      <div className={styles.cardRange}>{formatDateRange(request.range)}</div>
      <div className={styles.cardDays}>
        {formatBusinessDays(request.businessDays)}
        {balanceDays !== null && ` · saldo ${String(balanceDays)}`}
      </div>

      <div className={styles.cardTags}>
        <Chip
          background={work.length > 0 ? 'var(--color-warning-bg)' : 'var(--color-success-bg)'}
          foreground={work.length > 0 ? 'var(--color-warning-ink)' : 'var(--color-success-ink)'}
        >
          {work.length > 0
            ? `${String(work.length)} ${work.length === 1 ? 'pendiente' : 'pendientes'}`
            : 'Sin pendientes'}
        </Chip>
        {request.replacementName.length > 0 && (
          <span className={styles.replTag}>↺ {request.replacementName}</span>
        )}
        {/* Nombrar a alguien no es que haya aceptado, y la tarjeta lo dice
            antes de que el líder apruebe una cobertura que nadie confirmó. */}
        {request.replacementDecision === 'accepted' && (
          <span className={cx(styles.replState, styles.replAccepted)}>✓ acepta cubrirlo</span>
        )}
        {request.replacementDecision === 'pending' && (
          <span className={cx(styles.replState, styles.replPending)}>⏳ sin confirmar</span>
        )}
        {request.replacementDecision === 'declined' && (
          <span className={cx(styles.replState, styles.replDeclined)}>✕ no puede cubrirlo</span>
        )}
      </div>

      {/* El registro firmado de las solicitudes de antes, que sigue ahí para
          quien lo quiera leer. Las nuevas no traen ninguno y no pintan nada: ya
          no se pide. */}
      <RequestDocumentActions request={request} />

      {/* Said on the face of the card, not only inside the detail: whether this
          is somebody's third attempt changes how a leader reads the request, and
          they should not have to open a panel to find that out. */}
      {history.length > 0 && (
        <div className={styles.retryNote}>
          <span aria-hidden="true">↺</span>{' '}
          {history.length === 1 ? 'Segundo intento' : `Intento ${String(history.length + 1)}`} ·
          antes {history.map((previous) => previous.folio).join(', ')}
        </div>
      )}

      {request.requiredSignatures !== 2 && resuelta && firma !== undefined && (
        <p className={styles.decidedBy}>
          {firma.rejected ? 'Rechazada' : 'Aprobada'} por{' '}
          {/* El nombre en el mismo verde que los firmantes del bloque de dos, que es
              donde ya se había aprendido a leer «esto ya está firmado». */}
          <span
            className={cx(
              styles.decidedByName,
              firma.rejected && styles.decidedByNameRejected,
            )}
          >
            {firma.approverName}
          </span>
        </p>
      )}

      {/* Shown only when a second signature is required — one slot would read as a
          progress bar with a single step. */}
      {request.requiredSignatures === 2 && (
        <div className={styles.signatures}>
          <div className={styles.blockLabel}>Doble visto bueno</div>
          {trail.map((segment, index) => {
            /*
              A quién le toca cada hueco lo decide `pendingSignersFor`, por
              identidad. Antes se cogía `links[index]` —el líder que ocupara esa
              posición— y el orden de los vínculos no tiene nada que ver con el de
              las firmas: cuando el primero en firmar venía segundo en la lista,
              su nombre salía en los dos renglones.
            */
            const quien = pendientes[index] ?? null;
            const expected = quien === null ? undefined : links.find((l) => l.leaderPersonId === quien);
            const name = segment.approverName ?? (quien === null ? null : nameOfLeader(quien));
            const project =
              expected === undefined || expected.projectLabel.length === 0
                ? projectFor(name)
                : expected.projectLabel;

            return (
            <div key={segment.order} className={styles.signature}>
              <span
                className={cx(
                  styles.signatureMark,
                  segment.signed && styles.signatureDone,
                  segment.rejected && styles.signatureRejected,
                )}
                aria-hidden="true"
              >
                {segment.signed ? '✓' : segment.rejected ? '×' : segment.order}
              </span>
              <span
                className={cx(styles.signatureName, segment.signed && styles.signatureDoneName)}
              >
                {name ?? 'Pendiente de asignar'}
              </span>
              <span className={styles.signatureRole}>{project}</span>
            </div>
            );
          })}
        </div>
      )}

      <button type="button" className={styles.detailToggle} onClick={onToggleDetail}>
        {expanded ? 'Ocultar detalle' : 'Ver detalle de la solicitud'}
      </button>

      {expanded && (
        <div className={styles.detailPanel}>
          {history.length > 0 && (
            <div className={styles.detailGroup}>
              <span className={styles.detailLabel}>Intentos anteriores</span>
              {history.map((previous) => (
                <div key={previous.id} className={styles.workItem}>
                  {formatDateRange(previous.range)} · {previous.folio} · {previous.status}
                  {previous.resolutionReason !== null && ` — ${previous.resolutionReason}`}
                </div>
              ))}
            </div>
          )}

          <div className={styles.detailGroup}>
            <span className={styles.detailLabel}>Trabajo pendiente</span>
            {work.length === 0 ? (
              <div className={styles.workItem}>Nada registrado para esta ausencia.</div>
            ) : (
              work.map((item) => (
                <div key={item.id} className={styles.workItem}>
                  {item.ticket.length > 0 && (
                    <>
                      <strong className={styles.workTicket}>{item.ticket}</strong> ·{' '}
                    </>
                  )}
                  {item.text}
                </div>
              ))
            )}
          </div>

          {holds('ver_sos') && (
            <div className={styles.sosGroup}>
              <span className={styles.detailLabel}>Contactos de emergencia</span>
              {sos.map((contact, index) => (
                <div key={contact.id} className={styles.sosItem}>
                  <span className={styles.sosOrder}>{index + 1}º</span>
                  <span className={styles.sosBody}>
                    <span className={styles.sosName}>
                      {contact.name}{' '}
                      <span className={styles.sosRelation}>· {contact.relation}</span>
                    </span>
                    {/* El mismo formato que en el perfil: el número se guarda en
                        crudo y se agrupa al pintarlo, aquí y allá. */}
                    <span className={styles.sosPhone}>{formatStoredPhone(contact.phone)}</span>
                  </span>
                </div>
              ))}
              {sos.length < 2 && (
                <div className={styles.sosWarning}>
                  Tiene menos de dos contactos registrados: pídele completarlos en su perfil.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {request.resolutionReason !== null && (
        <div className={styles.motive}>Motivo: {request.resolutionReason}</div>
      )}

      {request.replacementDecision === 'declined' && request.replacementReason !== null && (
        <div className={styles.replNote}>
          {request.replacementName} no puede cubrirlo: {request.replacementReason}
          <span className={styles.replHint}>
            La persona tiene que elegir otro respaldo desde «Mis ausencias».
          </span>
        </div>
      )}

      {request.replacementWaivedReason !== null && (
        <div className={styles.replNote}>
          Aprobada sin confirmación del respaldo: {request.replacementWaivedReason}
        </div>
      )}

      {ownRequest && !decided && (
        <p className={styles.ownNote}>
          Es tu solicitud: la firma alguien más. Sigue su avance desde «Mis ausencias».
        </p>
      )}

      {!decided && mayDecide && (
        <div className={styles.cardFooter}>
          <button
            type="button"
            className={styles.moveBack}
            disabled={back === undefined}
            aria-label="Mover a la izquierda"
            onClick={() => {
              if (back !== undefined) onMove(back.status);
            }}
          >
            ←
          </button>
          <button
            type="button"
            className={styles.moveForward}
            style={cssVars(
              forward === null
                ? {}
                : { '--target-color': forward.color, '--target-ink': inkFor(forward.color) },
            )}
            disabled={forward === null}
            aria-label={forward === null ? 'Sin destino' : `Mover a ${forward.status}`}
            onClick={() => {
              if (forward !== null) onMove(forward.status);
            }}
          >
            {forwardLabel}
          </button>
        </div>
      )}

    </article>
  );
};

/**
 * Rejection.
 *
 * The reason is mandatory and catalogue-bound, and the dialog offers to compute
 * alternative windows — the handoff's answer to a rejection being a dead end.
 */
const RejectDialog = ({
  request,
  onClose,
  onDone,
}: {
  request: VacationRequest;
  onClose: () => void;
  onDone: () => Promise<void>;
}): ReactNode => {
  const session = useSession();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [suggest, setSuggest] = useState(true);
  const [busy, setBusy] = useState(false);

  const reasons = reasonsForScope(session.config.catalogs, 'vacaciones');

  const submit = async (): Promise<void> => {
    if (reason.length === 0) return;
    setBusy(true);

    const result = await getServices().requests.decide({
      requestId: request.id,
      decision: 'reject',
      reason,
      suggestedRanges: [],
      // Negar unos días nunca dependió de que alguien más los cubriera.
      waiveReplacementReason: null,
    });
    setBusy(false);

    if (!result.ok) {
      toast.bad('No se pudo registrar el rechazo.');
      return;
    }

    toast.warn(
      suggest
        ? `${request.folio} rechazada. Le sugerimos ventanas alternativas a ${request.personName}.`
        : `${request.folio} rechazada.`,
    );
    await onDone();
  };

  return (
    <Modal
      title={`Rechazar ${request.folio}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Volver
          </Button>
          <Button
            variant="danger"
            busy={busy}
            disabled={reason.length === 0}
            onClick={() => {
              void submit();
            }}
          >
            Rechazar
          </Button>
        </>
      }
    >
      <div className={shared.stack}>
        <p className={shared.rowNote}>
          {request.personName} pidió {formatDateRange(request.range)} ·{' '}
          {formatBusinessDays(request.businessDays)}.
        </p>

        <Field
          label="Motivo del rechazo"
          hint="Del catálogo, o escrito con tus palabras. Queda en la bitácora y se le muestra a la persona."
        >
          {(id) => (
            <div className={shared.stack}>
              <span className={shared.rowNote}>Motivos frecuentes</span>
              <ReasonPicker id={id} reasons={reasons} value={reason} onChange={setReason} />
            </div>
          )}
        </Field>

        <label className={shared.checkboxRow}>
          <input
            type="checkbox"
            checked={suggest}
            onChange={(event) => {
              setSuggest(event.target.checked);
            }}
          />
          <span>
            <strong>Proponer fechas alternativas</strong>{' '}
            Sugerirle ventanas alternativas de la misma duración, respetando el tope de su sub
            área y los feriados.
          </span>
        </label>
      </div>
    </Modal>
  );
};

/** Rendered when a leader has nothing waiting. */
export const EmptyBoard = (): ReactNode => (
  <EmptyState glyph="✓" title="Tu bandeja está limpia" note="No hay solicitudes por responder." />
);

/**
 * Undoing an approval so the request can be decided again.
 *
 * Distinct from the rejection dialog because it is a distinct act: the request is
 * not being turned down, it is being put back. The copy says what the move costs —
 * the signatures already given are annulled — so nobody discovers that by watching
 * the signature trail empty out afterwards.
 */
/**
 * La salida de una solicitud a la que se le pasaron las fechas sin decisión.
 *
 * Pregunta lo único que el portal no puede saber —si esa persona se fue esos
 * días— y cierra con esa respuesta: si sí, la aprueba tarde y los días pasan a
 * tomados; si no, la cancela y los devuelve al saldo. El motivo es obligatorio en
 * los dos caminos, porque es una decisión tomada fuera de tiempo sobre los días de
 * alguien más y la bitácora tiene que poder decir por qué.
 */
const OverdueDialog = ({
  request,
  onClose,
  onDone,
}: {
  request: VacationRequest;
  onClose: () => void;
  onDone: () => Promise<void>;
}): ReactNode => {
  const toast = useToast();
  const [outcome, setOutcome] = useState<'tomada' | 'no-tomada' | null>(null);
  const [reason, setReason] = useState('');
  const [sugerir, setSugerir] = useState(true);
  const [busy, setBusy] = useState(false);

  const enviar = (): void => {
    if (outcome === null || reason.trim().length === 0) return;
    setBusy(true);
    void getServices()
      .requests.resolveOverdue({
        requestId: request.id,
        outcome,
        reason: reason.trim(),
        suggestReschedule: outcome === 'no-tomada' && sugerir,
      })
      .then(async (result) => {
        setBusy(false);
        if (!result.ok) {
          toast.bad('No se pudo cerrar la solicitud.');
          return;
        }
        toast.ok(
          outcome === 'tomada'
            ? `${request.folio} quedó ${result.value.estado.toLowerCase()}.`
            : `${request.folio} se cerró y le devolvimos ${formatBusinessDays(
                result.value.diasDevueltos,
              )}.`,
        );
        await onDone();
      });
  };

  return (
    <Modal
      title={`Resolver ${request.folio}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Volver
          </Button>
          <Button
            busy={busy}
            disabled={outcome === null || reason.trim().length === 0}
            onClick={enviar}
          >
            {outcome === 'no-tomada' ? 'Cerrar y devolver los días' : 'Cerrar la solicitud'}
          </Button>
        </>
      }
    >
      <div className={shared.stack}>
        <p className={shared.rowNote}>
          {request.personName} pidió {formatDateRange(request.range)} ·{' '}
          {formatBusinessDays(request.businessDays)}, y esas fechas ya pasaron sin decisión. Sus
          días siguen apartados hasta que esto se cierre.
        </p>

        <Field label="¿Tomó esos días?">
          {(id) => (
            <div id={id} className={styles.overdueChoices}>
              <Button
                variant={outcome === 'tomada' ? 'primary' : 'secondary'}
                onClick={() => {
                  setOutcome('tomada');
                }}
              >
                Sí se fue
              </Button>
              <Button
                variant={outcome === 'no-tomada' ? 'primary' : 'secondary'}
                onClick={() => {
                  setOutcome('no-tomada');
                }}
              >
                No se fue
              </Button>
            </div>
          )}
        </Field>

        {outcome === 'tomada' && (
          <Banner tone="info">
            Se aprueba tarde y esos {formatBusinessDays(request.businessDays)} pasan a contar como
            tomados. Si el rango todavía no termina, queda aprobada y se cierra sola al terminar.
          </Banner>
        )}

        {outcome === 'no-tomada' && (
          <Banner tone="warn">
            Se cancela y {formatBusinessDays(request.businessDays)} vuelven a su saldo. Las fechas
            nuevas las elige {request.personName} con el asistente, que es quien comprueba saldo y
            traslapes.
          </Banner>
        )}

        {/* El motivo no se pide antes de elegir: sin la respuesta delante, «qué pasó»
            no tiene contra qué contestarse. */}
        {outcome !== null && (
          <Field
            label={outcome === 'tomada' ? 'Motivo del retraso' : 'Qué pasó'}
            hint="Queda en la solicitud y en la bitácora."
          >
            {(id) => (
              <textarea
                id={id}
                className={inputClass()}
                rows={3}
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                }}
              />
            )}
          </Field>
        )}

        {outcome === 'no-tomada' && (
          <label className={styles.overdueCheck}>
            <input
              type="checkbox"
              checked={sugerir}
              onChange={(event) => {
                setSugerir(event.target.checked);
              }}
            />
            Mandarle un correo con tres ventanas para volver a pedirlos
          </label>
        )}
      </div>
    </Modal>
  );
};

const ReopenDialog = ({
  request,
  status,
  onClose,
  onDone,
}: {
  request: VacationRequest;
  status: 'Por revisar' | 'En revisión';
  onClose: () => void;
  onDone: () => Promise<void>;
}): ReactNode => {
  const session = useSession();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const reasons = reasonsForScope(session.config.catalogs, 'vacaciones');

  return (
    <Modal
      title={`Reabrir ${request.folio}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Volver
          </Button>
          <Button
            busy={busy}
            disabled={reason.length === 0}
            onClick={() => {
              setBusy(true);
              void getServices()
                .requests.reopen({ requestId: request.id, status, reason })
                .then(async (result) => {
                  setBusy(false);
                  if (!result.ok) {
                    toast.bad('No se pudo reabrir la solicitud.');
                    return;
                  }
                  toast.warn(`${request.folio} volvió a ${status}.`);
                  await onDone();
                });
            }}
          >
            Reabrir
          </Button>
        </>
      }
    >
      <div className={shared.stack}>
        {/* Names the outcome it is coming out of rather than assuming an approval:
            a rejected request reopens too, and «tenía aprobado» would be a lie. */}
        <p className={shared.rowNote}>
          La solicitud de {request.personName} está {request.status.toLowerCase()} ·{' '}
          {formatDateRange(request.range)}. Al reabrirla se anulan las firmas ya registradas y los
          días vuelven a contar como pendientes.
        </p>
        <Field label="Motivo de la reapertura">
          {(id) => (
            <ReasonPicker id={id} reasons={reasons} value={reason} onChange={setReason} />
          )}
        </Field>
      </div>
    </Modal>
  );
};

/**
 * Aprobar sin que el respaldo haya confirmado.
 *
 * La salida del candado, y pide una frase por la misma razón por la que un
 * rechazo pide motivo: alguien va a leer esta solicitud dentro de seis meses
 * preguntándose quién cubría esos días, y «el líder marcó una casilla» no es una
 * respuesta. La frase queda en la solicitud, no sólo en la bitácora.
 */
const WaiveReplacementDialog = ({
  request,
  onClose,
  onConfirm,
}: {
  request: VacationRequest;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}): ReactNode => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const declined = request.replacementDecision === 'declined';

  return (
    <Modal
      title={`Aprobar ${request.folio} sin confirmación`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Esperar
          </Button>
          <Button
            variant="primary"
            busy={busy}
            disabled={reason.trim().length === 0}
            onClick={() => {
              setBusy(true);
              void onConfirm(reason.trim());
            }}
          >
            Aprobar de todos modos
          </Button>
        </>
      }
    >
      <div className={shared.stack}>
        <p className={shared.rowNote}>
          {declined
            ? `${request.replacementName} dijo que no puede cubrir estos días${
                request.replacementReason === null ? '' : `: ${request.replacementReason}`
              }`
            : `${request.replacementName} todavía no confirma que va a cubrir estos días.`}{' '}
          Puedes aprobar igualmente — lo razonable cuando la persona está de baja o
          ya lo acordaron fuera del portal —, pero queda escrito en la solicitud.
        </p>

        <Field label="Por qué apruebas sin la confirmación">
          {(id) => (
            <textarea
              id={id}
              className={styles.waiveText}
              rows={3}
              maxLength={500}
              placeholder="Ej. Ya lo acordamos en la junta de equipo; Deyanira cubre igual."
              value={reason}
              onChange={(event) => {
                setReason(event.target.value.slice(0, 500));
              }}
            />
          )}
        </Field>
      </div>
    </Modal>
  );
};
